import { environmentHttpClient } from "client/Api/httpClient";
import { ListLocationModel, DocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { Guid } from "client_legacy/ndaw/guid";

export const enum PrincipalType {
	None = 0,
	User = 1,
	DistributionList = 2,
	SecurityGroup = 3,
	SharePointGroup = 4,
	All = 5
}

export type SharePointPeoplePickerItemModel = {
	memberKey: string;
	name: string;
	principalType: PrincipalType;
};

const controllerRelativeUrl = "Documents/SharePoint/Sharing";

export function shareItem(
	selectedMember: SharePointPeoplePickerItemModel,
	item: DocumentItemViewModel,
	permission: SharedPermission,
	signal?: AbortSignal
) : Promise<void> {
	const location: ListLocationModel = convertToLocationModel(item.location);

	return environmentHttpClient.post<void>(
		controllerRelativeUrl + "/Share",
		{
			selectedMember,
			location,
			itemId: item.id,
			permission,
		},
		{ signal }
	);
}

export type ShareWithUserInfo = {
	canBeSharedByCurrentUser: boolean;
	isShareByEmailEnabled: boolean;
};

export function getShareWithUserInfo(
	location: ListLocationModel,
	itemId: number,
	signal?: AbortSignal
) : Promise<ShareWithUserInfo> {

	return environmentHttpClient.get<ShareWithUserInfo>(
		controllerRelativeUrl + "/GetShareWithUserInfo",
		{
			location,
			itemId
		},
		{ signal }
	);
}

export function findMembers(
	location: ListLocationModel,
	searchString: string,
	allowExternalEmails: boolean = true,
	signal?: AbortSignal
): Promise<SharePointPeoplePickerItemModel[]> {
	return environmentHttpClient.get<SharePointPeoplePickerItemModel[]>(
		controllerRelativeUrl + "/FindMembers",
		{
			location,
			searchString,
			allowExternalEmails
		},
		{ signal }
	);
}

export enum SharePointMemberType {
	User = 1,
	Group = 2
}

export enum SharedPermission {
	Undefined = 0,
	CanView = 1,
	CanEdit = 2,
	StopSharing = 3
}

export type SharePointMember = {
	id: number;
	name: string;
	type: SharePointMemberType;
};

export type LinkInformation = {
	canBeChanged: boolean;
} & (
		{
			isEnabled: false;
		}
		|
		{
			isEnabled: true;
			kind: SharedLinkKind;
			id: Guid;
			url: string;
		}
	);

export type AnonymousLinkInformation = LinkInformation & {
	expirationDate?: Date | string;
};

export type SpecificPeopleLinkInformation = LinkInformation & {
	members: ReadonlyArray<string>;
};

export type SharedLinksInformation = {
	isCompletelyUnavailable: boolean;
	areAnonymousLinksAllowed: boolean;
	isShareByEmailEnabled: boolean;
	defaultLinkKind: DefaultLinkKind;
	anonymousView: AnonymousLinkInformation;
	anonymousEdit: AnonymousLinkInformation;
	organizationView: LinkInformation;
	organizationEdit: LinkInformation;
	specificPeopleView: SpecificPeopleLinkInformation;
	specificPeopleEdit: SpecificPeopleLinkInformation;
	peopleWithExistingAccessUrl: string | null;
};

export enum DefaultLinkKind {
	AnonymousReadOnly = 1,
	AnonymousEdit = 2,
	OrganizationReadOnly = 3,
	OrganizationEdit = 4,
	PeopleWithExistingAccess = 5
}

export enum SharedLinkKind {
	AnonymousReadOnly = 1,
	AnonymousEdit = 2,
	OrganizationReadOnly = 3,
	OrganizationEdit = 4,
	Flexible = 5
}

export type AnonymousLinkDataModel = {
	expirationDate: OptionalModel<string>;
	allowEditing: boolean;
};

export type OrganisationLinkDataModel = {
	allowEditing: boolean;
};

export type SpecificPeopleLinkDataModel = {
	selectedPeople: readonly SharePointPeoplePickerItemModel[];
	allowEditing: boolean;
};

export type EnableAnonymousLinkModel = {
	location: ListLocationModel;
	itemId: number;
	data: AnonymousLinkDataModel;
};

export type EnableOrganisationLinkModel = {
	location: ListLocationModel;
	itemId: number;
	data: OrganisationLinkDataModel;
};

export type EnableSpecificPeopleLinkModel = {
	location: ListLocationModel;
	itemId: number;
	data: SpecificPeopleLinkDataModel;
};

export function getShareFileInfo(
	location: ListLocationModel,
	itemId: number,
	signal?: AbortSignal
): Promise<SharedLinksInformation> {
	return environmentHttpClient.get<SharedLinksInformation>(
		controllerRelativeUrl + "/GetShareFileInfo",
		{
			location,
			itemId
		},
		{ signal }
	);
}

export function enableAnonymousLink(
	model: EnableAnonymousLinkModel,
	signal?: AbortSignal
): Promise<string> {
	return environmentHttpClient.put<string>(
		controllerRelativeUrl + "/EnableAnonymousLink",
		model,
		{ signal }
	);
}

export function enableOrganisationLink(
	model: EnableOrganisationLinkModel,
	signal?: AbortSignal
): Promise<string> {
	return environmentHttpClient.put<string>(
		controllerRelativeUrl + "/EnableOrganisationLink",
		model,
		{ signal }
	);
}

export function enableSpecificPeopleLink(
	model: EnableSpecificPeopleLinkModel,
	signal?: AbortSignal
): Promise<string> {
	return environmentHttpClient.put<string>(
		controllerRelativeUrl + "/EnableSpecificPeopleLink",
		model,
		{ signal }
	);
}

export type DisableLinkModel = {
	location: ListLocationModel;
	itemId: number;
	kind: SharedLinkKind;
	shareId: Guid | null;
};

export function disableLink(
	model: DisableLinkModel,
	signal?: AbortSignal
)
{
	return environmentHttpClient.put<void>(
		controllerRelativeUrl + "/DisableLink",
		model,
		{ signal }
	);
}

export type SharedSetting = {
	member: SharePointMember;
	permission: SharedPermission;
	enabled: boolean;
};

export type SharedSettingsSaveModel = {
	settings: readonly SharedSetting[];
};

export function getShareSettings(
	id: number,
	location: ListLocationModel,
	signal?: AbortSignal
): Promise<SharedSetting[]> {
	return environmentHttpClient.get<SharedSetting[]>(
		controllerRelativeUrl + "/GetShareSettings",
		{
			id,
			location,
		},
		{ signal }
	);
}

export function setShareSettings(
	id: number,
	location: ListLocationModel,
	model: SharedSettingsSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.post<void>(
		controllerRelativeUrl + "/SetShareSettings",
		{
			id,
			location,
			model,
		},
		{ signal }
	);
}
